import { useRef, useState } from 'react';
import { useGT } from 'gt-react';
import type {
  DataAssetResponse,
  ImportFieldNode,
  StatefulTableDefinitionResponse,
} from '@erp/rpc';
import {
  useCreateDataAsset,
  useConfirmDataAssetUpload,
  useListDataAssets,
  useGetVersionSignedUrl,
  useValidateDataAssetFields,
} from 'src/api/rpc/hooks/data-assets/useDataAssets';
import {
  useCreateStatefulTable,
  useDeleteStatefulTable,
  useListStatefulTables,
} from 'src/api/rpc/hooks/stateful-tables/useStatefulTables';
import {
  useCreateDataMapping,
  useSyncDataMappingFromSource,
} from 'src/api/rpc/hooks/data-mappings/useDataMappings';
import {
  prepareUploadFile,
  putToSignedUrl,
  uploadErrorMessage,
} from '../data-assets/upload';
import { suggestSchemaForVersion } from '../data-assets/assetContent';
import type { SampledRows } from '../data-assets/sampleForSchema';
import { useSuggestName } from 'src/api/rpc/hooks/core/useSuggestName';
import {
  columnDraftsFromImportFields,
  emptyColumnDraft,
  mappingNameFromFileName,
  slugifyMappingName,
  type ColumnDraft,
} from './columnDrafts';
import {
  runCreateDataMappingFlow,
  type CreateFlowResult,
} from './createDataMappingFlow';
import {
  liveKeyCandidates,
  suggestKeyColumns,
  type KeyCandidate,
} from './suggestKeyColumns';
import type {
  SourceMode,
  TableMode,
  WizardSource,
} from './DataMappingWizardPanels';

/**
 * The create-a-data-mapping wizard's state, one hook per step plus a composer.
 * Splitting it this way keeps each piece small enough to read on its own; the
 * composed shape is what the panels and the step list consume.
 *
 * The branching that decides *what* should happen lives in the tested pure
 * modules (`wizardStepChecks`, `createDataMappingFlow`) — these hooks only hold
 * state and run the side effects.
 */

interface SourceStepState {
  sourceMode: SourceMode;
  setSourceMode: (mode: SourceMode) => void;
  file: File | null;
  handleFileChange: (file: File | null) => void;
  assetName: string;
  setAssetName: (name: string) => void;
  existingAssetId: string;
  setExistingAssetId: (id: string) => void;
  readyAssets: DataAssetResponse[];
  source: WizardSource | null;
  uploading: boolean;
  uploadError: string | null;
  uploadSource: () => Promise<boolean>;
  applyExistingSource: () => boolean;
}

function useSourceStep(
  gt: ReturnType<typeof useGT>,
  /** Called with the file name whenever a source is recorded. */
  onSourceChosen: (fileName: string) => void,
): SourceStepState {
  const [sourceMode, setSourceMode] = useState<SourceMode>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [assetName, setAssetName] = useState('');
  const [existingAssetId, setExistingAssetId] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [source, setSource] = useState<WizardSource | null>(null);

  const { data: assets } = useListDataAssets();
  const createAsset = useCreateDataAsset();
  const confirmUpload = useConfirmDataAssetUpload();

  const readyAssets = (assets?.dataAssets ?? []).filter(
    (asset) => asset.currentVersionId,
  );

  /** Uploads the picked file as a new data asset and records it as the source. */
  async function uploadSource(): Promise<boolean> {
    if (!file) return false;
    setUploadError(null);
    setUploading(true);

    const prepared = await prepareUploadFile(file).catch(() => null);
    if (!prepared) {
      setUploading(false);
      setUploadError(gt('Could not read that file.'));
      return false;
    }

    try {
      const created = await createAsset.mutateAsync({
        name: assetName.trim() || prepared.fileName,
        fileName: prepared.fileName,
        contentType: prepared.contentType,
      });
      await putToSignedUrl(
        created.uploadUrl,
        prepared.blob,
        prepared.contentType,
      );
      await confirmUpload.mutateAsync({
        versionId: created.version.id,
        fileSize: prepared.blob.size,
      });
      setSource({
        dataAssetId: created.dataAsset.id,
        dataAssetVersionId: created.version.id,
        contentType: prepared.contentType,
        fileName: prepared.fileName,
      });
      onSourceChosen(prepared.fileName);
      setFile(null);
      setUploading(false);
      return true;
    } catch (error) {
      setUploading(false);
      setUploadError(uploadErrorMessage(error));
      return false;
    }
  }

  return {
    sourceMode,
    setSourceMode,
    file,
    handleFileChange: (picked) => {
      setFile(picked);
      setSource(null);
      if (picked && !assetName) setAssetName(picked.name);
    },
    assetName,
    setAssetName,
    existingAssetId,
    setExistingAssetId,
    readyAssets,
    source,
    uploading,
    uploadError,
    uploadSource,
    /** Records an already-uploaded asset as the source. */
    applyExistingSource: () => {
      const asset = readyAssets.find((a) => a.id === existingAssetId);
      if (!asset?.currentVersionId || !asset.currentVersion) return false;
      setSource({
        dataAssetId: asset.id,
        dataAssetVersionId: asset.currentVersionId,
        contentType: asset.currentVersion.contentType,
        fileName: asset.currentVersion.fileName,
      });
      // The asset's own name reads better than the stored file name here — it
      // is what a person typed when the asset was created.
      onSourceChosen(asset.name || asset.currentVersion.fileName);
      return true;
    },
  };
}

interface SchemaStepState {
  columns: ColumnDraft[];
  keyColumnIds: string[];
  /** Adds or removes a column from the key, preserving key order. */
  toggleKeyColumn: (id: string) => void;
  keyColumnNames: string[];
  columnErrors: Record<string, string>;
  setColumnErrors: (errors: Record<string, string>) => void;
  addColumn: () => void;
  changeColumn: (id: string, patch: Partial<ColumnDraft>) => void;
  removeColumn: (id: string) => void;
  suggesting: boolean;
  suggestError: string | null;
  suggestNotes: string[];
  handleSuggestColumns: () => Promise<void>;
  /**
   * Rows parsed from the source during inference; null until inferred, for
   * JSON, or once the source moved on from the version they were read from.
   */
  sample: SampledRows | null;
  /**
   * Keys the sampled rows prove unique, narrowest first — found by the same
   * unique-tuple discovery the mapping detail page runs, but at inference time.
   * Empty until an inference produces a row sample, and again once the source or
   * the named columns change out from under the proof.
   */
  keyCandidates: KeyCandidate[];
  /** Replaces the key selection with a suggested candidate. */
  applyKeyCandidate: (candidate: KeyCandidate) => void;
}

function useSchemaStep(
  gt: ReturnType<typeof useGT>,
  source: WizardSource | null,
): SchemaStepState {
  const [columns, setColumns] = useState<ColumnDraft[]>([emptyColumnDraft()]);
  const [keyColumnIds, setKeyColumnIds] = useState<string[]>([]);
  const [columnErrors, setColumnErrors] = useState<Record<string, string>>({});
  const [suggesting, setSuggesting] = useState(false);
  const [suggestNotes, setSuggestNotes] = useState<string[]>([]);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  /**
   * What the last inference learned, stamped with the asset version it read.
   * Kept as one object so a stale sample and stale candidates can never be
   * shown independently, and compared against the current source so switching
   * files discards both without an effect to sync them.
   */
  const [inference, setInference] = useState<{
    dataAssetVersionId: string;
    sample: SampledRows | null;
    keyCandidates: KeyCandidate[];
  } | null>(null);
  const liveInference =
    inference && inference.dataAssetVersionId === source?.dataAssetVersionId
      ? inference
      : null;

  const getUrl = useGetVersionSignedUrl();
  const validateFields = useValidateDataAssetFields();

  /**
   * Installs the inferred columns and picks the key from the sampled rows.
   * Returns any source headers the inference could not carry as columns.
   */
  function applySuggestedFields(
    fields: ImportFieldNode[],
    inferredSample: SampledRows | null,
    dataAssetVersionId: string,
  ): string[] {
    const { drafts, skippedFieldNames } = columnDraftsFromImportFields(fields);
    if (drafts.length === 0) return skippedFieldNames;
    setColumns(drafts);

    const candidates = suggestKeyColumns(inferredSample, drafts);
    setInference({
      dataAssetVersionId,
      sample: inferredSample,
      keyCandidates: candidates,
    });
    // The narrowest proven key when the rows prove one; otherwise the first
    // column, the usual identifier, and the uniqueness warning says why it is
    // not enough on its own.
    setKeyColumnIds(candidates[0]?.columnIds ?? [drafts[0].id]);
    setColumnErrors({});
    return skippedFieldNames;
  }

  /**
   * Infers columns from the source file and proves them against the whole file
   * first, so a suggestion is either known-good or arrives with the reasons it
   * isn't. Reuses the data-asset schema suggestion path verbatim.
   */
  async function handleSuggestColumns() {
    if (!source) return;
    setSuggestError(null);
    setSuggestNotes([]);
    setSuggesting(true);

    const outcome = await suggestSchemaForVersion({
      versionId: source.dataAssetVersionId,
      contentType: source.contentType,
      fileName: source.fileName,
      getDownloadUrl: async (versionId) =>
        (await getUrl.mutateAsync({ versionId })).downloadUrl,
      validateFields: async (fields) =>
        (
          await validateFields.mutateAsync({
            dataAssetId: source.dataAssetId,
            fields,
          })
        ).report,
    });

    setSuggesting(false);
    if (!outcome.ok) {
      setSuggestError(outcome.error);
      return;
    }

    const skipped = applySuggestedFields(
      outcome.fields,
      outcome.sample,
      source.dataAssetVersionId,
    );
    setSuggestNotes([...outcome.warnings, ...skippedNote(skipped, gt)]);
  }

  return {
    columns,
    keyColumnIds,
    toggleKeyColumn: (id) =>
      setKeyColumnIds((current) =>
        current.includes(id)
          ? current.filter((keyId) => keyId !== id)
          : [...current, id],
      ),
    keyColumnNames: keyColumnIds.flatMap((id) => {
      const column = columns.find((candidate) => candidate.id === id);
      return column ? [column.name] : [];
    }),
    columnErrors,
    setColumnErrors,
    addColumn: () => setColumns((current) => [...current, emptyColumnDraft()]),
    changeColumn: (id, patch) =>
      setColumns((current) =>
        current.map((column) =>
          column.id === id ? { ...column, ...patch } : column,
        ),
      ),
    removeColumn: (id) => {
      setColumns((current) => current.filter((column) => column.id !== id));
      setKeyColumnIds((current) => current.filter((keyId) => keyId !== id));
    },
    suggesting,
    suggestError,
    suggestNotes,
    handleSuggestColumns,
    sample: liveInference?.sample ?? null,
    keyCandidates: liveInference
      ? liveKeyCandidates(liveInference.keyCandidates, columns)
      : [],
    applyKeyCandidate: (candidate) => setKeyColumnIds(candidate.columnIds),
  };
}

/** A note naming headers the inference dropped, or nothing when none were. */
function skippedNote(
  skipped: string[],
  gt: ReturnType<typeof useGT>,
): string[] {
  if (skipped.length === 0) return [];
  return [
    gt(
      'These source columns were left out because their names collide with another column: {names}.',
      { names: skipped.join(', ') },
    ),
  ];
}

interface TableStepState {
  tableMode: TableMode;
  setTableMode: (mode: TableMode) => void;
  /**
   * The mapping name until the user types a different one — one name for the
   * pair is what people mean by default, and the two drifting apart is the
   * exception worth an explicit keystroke.
   */
  tableName: string;
  setTableName: (name: string) => void;
  tableDescription: string;
  setTableDescription: (description: string) => void;
  existingTableId: string;
  setExistingTableId: (id: string) => void;
  tables: StatefulTableDefinitionResponse[];
  provisionedTableId: string | null;
  setProvisionedTableId: (id: string | null) => void;
}

function useTableStep(mappingName: string): TableStepState {
  const [tableMode, setTableMode] = useState<TableMode>('new');
  /** Null while the table name still follows the mapping name. */
  const [tableNameOverride, setTableNameOverride] = useState<string | null>(
    null,
  );
  const [tableDescription, setTableDescription] = useState('');
  const [existingTableId, setExistingTableId] = useState('');
  /**
   * A stateful table this flow created but has not yet linked to a mapping. Set
   * only when the mapping call failed and the table could not be dropped, so a
   * retry reuses it rather than colliding with its own leftover.
   */
  const [provisionedTableId, setProvisionedTableId] = useState<string | null>(
    null,
  );
  const { data: tables } = useListStatefulTables({ perPage: 100 });

  return {
    tableMode,
    setTableMode,
    tableName: tableNameOverride ?? mappingName,
    // Clearing the field puts it back on the mapping name rather than leaving
    // the table nameless — the mapping name is the default, not a one-time fill.
    setTableName: (name) => setTableNameOverride(name === '' ? null : name),
    tableDescription,
    setTableDescription,
    existingTableId,
    setExistingTableId,
    tables: tables?.results ?? [],
    provisionedTableId,
    setProvisionedTableId,
  };
}

interface MappingStepState {
  mappingName: string;
  handleMappingNameChange: (name: string) => void;
  /**
   * Fills the name from the source file, unless the user already typed one.
   * Called when a source is chosen, so the name is there by the table step —
   * which takes its own default from it.
   */
  applyFileNameDefault: (fileName: string) => void;
  /** Installs a name the user did not type (an AI suggestion). */
  applySuggestedName: (name: string) => void;
  mappingSlug: string;
  handleMappingSlugChange: (slug: string) => void;
  mappingDescription: string;
  setMappingDescription: (description: string) => void;
  syncNow: boolean;
  setSyncNow: (syncNow: boolean) => void;
}

function useMappingStep(): MappingStepState {
  const [mappingName, setMappingName] = useState('');
  const [mappingSlug, setMappingSlug] = useState('');
  /** Only read inside handlers, so a ref avoids a render per keystroke. */
  const slugEdited = useRef(false);
  const nameEdited = useRef(false);
  const [mappingDescription, setMappingDescription] = useState('');
  const [syncNow, setSyncNow] = useState(true);

  /** Sets the name and, while the slug is untouched, keeps the slug in step. */
  function setNameAndSlug(value: string) {
    setMappingName(value);
    if (!slugEdited.current) setMappingSlug(slugifyMappingName(value));
  }

  return {
    mappingName,
    handleMappingNameChange: (value) => {
      nameEdited.current = true;
      setNameAndSlug(value);
    },
    applyFileNameDefault: (fileName) => {
      if (nameEdited.current) return;
      const derived = mappingNameFromFileName(fileName);
      if (derived) setNameAndSlug(derived);
    },
    applySuggestedName: (name) => setNameAndSlug(name.trim()),
    mappingSlug,
    handleMappingSlugChange: (slug) => {
      slugEdited.current = true;
      setMappingSlug(slug);
    },
    mappingDescription,
    setMappingDescription,
    syncNow,
    setSyncNow,
  };
}

export interface DataMappingWizardState
  extends
    SourceStepState,
    SchemaStepState,
    Omit<TableStepState, 'provisionedTableId' | 'setProvisionedTableId'>,
    MappingStepState {
  submitError: string | null;
  submitting: boolean;
  handleComplete: () => Promise<void>;
  /** True while any request the wizard owns is in flight. */
  busy: boolean;
  /** Asks a model to name the mapping after the file and its columns. */
  handleSuggestMappingName: () => Promise<void>;
  /** True while a name suggestion is in flight. */
  namingSuggestion: boolean;
  nameSuggestError: string | null;
  /** False when there is nothing to name the mapping from yet. */
  canSuggestMappingName: boolean;
}

export function useDataMappingWizardState(
  onCreated: (dataMappingId: string) => void,
): DataMappingWizardState {
  const gt = useGT();
  const mappingStep = useMappingStep();
  const sourceStep = useSourceStep(gt, mappingStep.applyFileNameDefault);
  const schemaStep = useSchemaStep(gt, sourceStep.source);
  const tableStep = useTableStep(mappingStep.mappingName);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [nameSuggestError, setNameSuggestError] = useState<string | null>(null);
  const { suggestName, isPending: namingSuggestion } = useSuggestName();

  const namedColumns = schemaStep.columns.filter(
    (column) => column.name.trim().length > 0,
  );
  const canSuggestMappingName =
    !!sourceStep.source || namedColumns.length > 0 || !!schemaStep.sample;

  /**
   * Names the mapping from what the file actually is: its name plus the columns
   * inferred from it. With a name already in the box the call becomes a rename,
   * so the model improves on it rather than starting cold.
   */
  async function handleSuggestMappingName() {
    setNameSuggestError(null);
    const current = mappingStep.mappingName.trim();
    try {
      const name = await suggestName(
        {
          fileName: sourceStep.source?.fileName ?? sourceStep.file?.name,
          sourceHeaders: schemaStep.sample?.header,
          columns: namedColumns.map((column) => ({
            name: column.name.trim(),
            type: column.type,
            description: column.description.trim() || undefined,
          })),
          keyColumns: schemaStep.keyColumnNames,
        },
        {
          kind: 'data mapping',
          context:
            'A data mapping is a lookup table the ERP resolves by key. Name it for what a person looks up in it, not after the file format.',
          rename: current
            ? {
                currentName: current,
                reason:
                  'it was derived from the file name, before the columns were known',
              }
            : undefined,
        },
      );
      mappingStep.applySuggestedName(name);
    } catch {
      setNameSuggestError(gt('Could not suggest a name. Type one instead.'));
    }
  }

  const createTable = useCreateStatefulTable();
  const deleteTable = useDeleteStatefulTable();
  const createMapping = useCreateDataMapping();
  const syncFromSource = useSyncDataMappingFromSource();

  /** Runs the create sequence and translates its result into UI state. */
  async function handleComplete() {
    setSubmitError(null);
    setSubmitting(true);

    const result = await runCreateDataMappingFlow(
      {
        tableMode: tableStep.tableMode,
        existingTableId: tableStep.existingTableId,
        provisionedTableId: tableStep.provisionedTableId,
        columns: schemaStep.columns,
        keyColumnIds: schemaStep.keyColumnIds,
        tableName: tableStep.tableName,
        tableDescription: tableStep.tableDescription,
        mappingName: mappingStep.mappingName,
        mappingSlug: mappingStep.mappingSlug,
        mappingDescription: mappingStep.mappingDescription,
        source: sourceStep.source,
        syncNow: mappingStep.syncNow,
      },
      {
        createTable: async (input) => ({
          statefulTableId: (
            await createTable.mutateAsync({
              name: input.name,
              description: input.description,
              columns: input.columns as Parameters<
                typeof createTable.mutateAsync
              >[0]['columns'],
              keyColumns: input.keyColumns,
            })
          ).definition.id,
        }),
        createMapping: async (input) => ({
          dataMappingId: (await createMapping.mutateAsync(input)).dataMapping
            .id,
        }),
        deleteTable: async (id) => {
          await deleteTable.mutateAsync({ id });
        },
        syncFromSource: async (id) => {
          await syncFromSource.mutateAsync({ id });
        },
      },
    );

    setSubmitting(false);
    tableStep.setProvisionedTableId(
      result.ok ? null : result.provisionedTableId,
    );

    if (result.ok) {
      onCreated(result.dataMappingId);
      return;
    }
    setSubmitError(createFailureMessage(result, gt));
  }

  return {
    ...sourceStep,
    ...schemaStep,
    ...tableStep,
    ...mappingStep,
    submitError,
    submitting,
    handleComplete,
    busy:
      sourceStep.uploading ||
      schemaStep.suggesting ||
      submitting ||
      namingSuggestion,
    handleSuggestMappingName,
    namingSuggestion,
    nameSuggestError,
    canSuggestMappingName,
  };
}

/** Turns a create-flow failure into the message shown on the last step. */
function createFailureMessage(
  result: Extract<CreateFlowResult, { ok: false }>,
  gt: ReturnType<typeof useGT>,
): string {
  if (result.kind === 'invalid-columns') {
    return result.message ?? gt('Fix the columns before creating.');
  }
  if (result.kind === 'no-key-column') {
    return gt('Choose which column is the lookup key.');
  }
  return result.error instanceof Error
    ? result.error.message
    : gt('Could not create the data mapping.');
}
